import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowRight, ShieldCheck, Phone, ArrowDown } from "lucide-react";
import { HERO_IMAGE, CONTACT } from "../lib/constants";
import { Button } from "./ui/button";
import { MaskedLines, Magnetic, EASE } from "./motion";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  // Subtle 3D tilt driven by the cursor
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-5, 5]), {
    stiffness: 120,
    damping: 18,
  });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="inicio"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="relative min-h-[100svh] flex items-end">
        {/* Parallax backdrop */}
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <motion.img
            src={HERO_IMAGE}
            alt="Trabajador con equipo de protección personal"
            className="h-[115%] w-full object-cover"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1.02 }}
            transition={{ duration: 2.4, ease: EASE }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/60 to-emerald-950/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-emerald-950/30" />
        <div className="absolute inset-0 bg-blueprint-dark mix-blend-overlay opacity-40" />

        {/* Floating 3D certification card */}
        <motion.div
          style={{ rotateX, rotateY, transformPerspective: 900 }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 1, ease: EASE }}
          className="absolute right-10 top-[24%] hidden xl:block"
        >
          <div
            data-testid="hero-cert-card"
            className="w-64 rounded-sm border border-white/20 bg-white/10 p-6 text-white backdrop-blur-xl"
          >
            <div className="overline text-pv-lime mb-3">Certificación</div>
            <div className="font-display font-extrabold text-5xl tracking-tight">
              100%
            </div>
            <p className="mt-2 text-sm leading-relaxed text-white/75">
              Equipo certificado bajo normas internacionales ANSI, EN y NOM.
            </p>
            <div className="mt-4 h-px bg-white/20" />
            <div className="mt-4 flex items-center gap-2 text-xs text-white/70">
              <ShieldCheck size={14} className="text-pv-orange" />
              Respaldo técnico permanente
            </div>
          </div>
        </motion.div>

        {/* Kinetic content */}
        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-40 lg:px-8 lg:pb-28"
        >
          <div className="max-w-4xl text-white">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="mb-7 inline-flex items-center gap-2 rounded-sm border border-white/30 px-3 py-1.5"
            >
              <ShieldCheck size={14} className="text-pv-lime" />
              <span className="overline text-white/90">
                23 años protegiendo Costa Rica
              </span>
            </motion.div>

            <MaskedLines
              onLoad
              delay={0.25}
              className="font-display font-extrabold leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-8xl"
              lines={[
                <>Seguridad que</>,
                <span className="text-pv-lime">protege vidas.</span>,
                <>Equipos que cuidan</>,
                <span className="text-pv-orange">empresas.</span>,
              ]}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
              className="mt-7 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg"
            >
              Somos especialistas en salud ocupacional. Analizamos sus riesgos,
              asesoramos a su equipo y proveemos el equipo de protección personal
              certificado que su empresa necesita —de la cabeza a los pies.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
              className="mt-10 flex flex-col gap-3 sm:flex-row"
            >
              <Magnetic>
                <Button
                  asChild
                  data-testid="hero-cta-quote"
                  className="group h-14 rounded-sm bg-pv-orange px-7 text-base font-semibold text-white hover:bg-pv-orange/90"
                >
                  <a href="#contacto">
                    Solicitar Cotización
                    <ArrowRight
                      size={18}
                      className="ml-2 transition-transform group-hover:translate-x-1"
                    />
                  </a>
                </Button>
              </Magnetic>
              <Magnetic>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-cta-call"
                  className="h-14 rounded-sm border-white/40 bg-transparent px-7 text-base font-semibold text-white hover:bg-white hover:text-pv-green"
                >
                  <a href={`tel:+506${CONTACT.phone.replace("-", "")}`}>
                    <Phone size={18} className="mr-2" />
                    Llamar al {CONTACT.phone}
                  </a>
                </Button>
              </Magnetic>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/60 lg:flex"
        >
          <span className="overline text-[10px]">Deslice</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <ArrowDown size={16} />
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
